import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";
import { useState } from "react";

import { ColorContext } from "@/components/canvas-editor/components/Container";
import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  checkAuth,
  getCurrentUser,
  getLoginUrl,
  LoginSuccessType,
  setLoginSuccessType,
} from "@/lib/api";
import { removeInvisibleChars } from "@/lib/remove-invisible-chars";

export const Route = createFileRoute("/atprotofans/login")({
  component: LoginPage,
  beforeLoad: async () => {
    let isLoggedIn = false;

    try {
      await checkAuth();
      await getCurrentUser();
      isLoggedIn = true;
    } catch {
      // not logged in
    }

    if (isLoggedIn) {
      throw redirect({ to: "/atprotofans/connect-stripe" });
    }
  },
  head: () => ({ meta: [{ title: "Login" }] }),
});

function LoginPage() {
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const onLogin = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoginSuccessType(LoginSuccessType.AT_PROTOFANS_STRIPE);

      const loginUrl = await getLoginUrl();
      const formData = new FormData(e.target as HTMLFormElement);
      const username = removeInvisibleChars(formData.get("username") as string);

      window.location.href = `${loginUrl.replace(
        "http://",
        "https://",
      )}&redirect=${encodeURIComponent(
        "/atprotofans/connect-stripe",
      )}&login_hint=${username}`;
    },
    onError: (error) => {
      toast({
        title: "Error Logging In",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <main className="flex flex-col min-h-screen relative">
      <div className="flex-1 flex justify-center items-center h-full bg-teal-11 dark:bg-tealdark-10">
        <div className="px-4 flex flex-col items-center text-slate-normal">
          <div className="relative @container self-stretch flex flex-col gap-2">
            <div className="text-slate-12 dark:text-slatedark-12 text-[30cqw] mb-12 leading-none flex justify-center">
              <span className="instrument-sans">ATProto</span>
              <span className="instrument-serif italic">Fans</span>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center pb-20">
            <form
              className="px-6 max-w-md w-full flex flex-col items-center gap-8"
              onSubmit={onLogin.mutate}
            >
              <ColorContext value="blue">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 justify-center">
                    <label
                      htmlFor="username"
                      className="text-center text-sm font-semibold leading-none text-teal-contrast dark:text-tealdark-contrast"
                    >
                      Bluesky/Atproto Username
                    </label>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" className="p-0" size="smIcon">
                          <HelpCircle className="text-teal-contrast dark:text-tealdark-contrast" />
                          <span className="sr-only">Help</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Help</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col gap-2">
                          <p>
                            To login to graze you need a Bluesky account (or any
                            account on Atproto).
                          </p>
                          <p>
                            In the form on this page, enter your username you
                            use on Bluesky. You will then be redirected to
                            Bluesky where you can log in.
                          </p>
                          <p>
                            On that page you are <em>on Bluesky</em> and you
                            will log in with your Bluesky password.
                          </p>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="user.bsky.social"
                      name="username"
                      id="username"
                      autoComplete="username"
                      className="text-center max-w-xs lg:w-[50vw] text-lg! placeholder:text-lg! h-12!"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col justify-center items-center gap-4 w-full">
                  <Button className="relative" type="submit" size="lg">
                    Login
                  </Button>
                </div>
              </ColorContext>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
